import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import styled, { css } from 'styled-components';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import { BREAKPOINT, REFRESH_ICON } from '@constants';
import { useCountry } from '@hooks/useCountry';
import ChevronDownIcon from '@icons/chevron-down.svg';
import ChevronUpIcon from '@icons/chevron-up.svg';
import { replaceText } from '@utils/handleTextReplacement';
import { getTranslatedHtml } from '@utils/html';
import Colors from '../Colors';
import ExpandableContent from '../ExpandableContent';
import { TextVariant, Typography, TEXT_STYLES } from './Typography';
import { renderIcon } from './utils/icons';

interface DidYouKnowProps {
  text: string | string[] | ReactNode;
  headingText?: string | null;
  moreInfoText?: string;
  onMoreInfoClick?: VoidFunction;
  centered?: boolean;
  expanded?: boolean;
  expandable?: boolean;
  compact?: boolean;
  background?: string;
  borderRadius?: string;
  borderColor?: string;
  icon?: React.ReactNode | 'greeting' | 'did-you-know' | 'info' | 'warning' | 'error' | 'hourglass' | 'nurse' | 'order';
  moreInfoIcon?: React.ReactNode | 'greeting' | 'did-you-know' | 'info' | 'warning' | 'error' | 'hourglass' | 'nurse' | 'order';
  fullWidth?: boolean;
  localisationCounty?: CountryCode;
  className?: string;
}

/** Props for DidYouKnow component:
  #
  # @param {string | string[] | ReactNode} text - The main content text to display. Can be a single string or array of alternating texts, or a whole component
  # @param {string} [headingText] - Optional header text to show at the top of the component
  # @param {string | ReactNode} [icon] - Icon to show on the left. Can be predefined string ('greeting', 'did-you-know', etc) or custom ReactNode
  # @param {string | ReactNode} [moreInfoIcon] - Icon to show on the bottom. Can be predefined string ('info', '', etc) or custom ReactNode
  # @param {string} [moreInfoText] - Optional text to show at the bottom of the component. Used to open a modal with more information
  # @param {VoidFunction} [onMoreInfoClick] - Optional callback function triggered when the more info section is clicked
  # @param {boolean} [centered] - Whether to center align the content and header text
  # @param {boolean} [expanded] - Controls if expandable content is shown initially. Only used when expandable=true
  # @param {boolean} [expandable] - Whether the content can be expanded/collapsed with a click
  # @param {boolean} [compact] - Reduces padding between header and body when true
  # @param {string} [borderRadius] - Custom border radius, defaults to '5px'
  # @param {string} [borderColor] - Custom border color, defaults to 'transparent'
  # @param {string} [background] - Background color, defaults to Colors.mint50
  # @param {boolean} [fullWidth] - Whether component should take full width of container
  # @param {string} [localisationCounty] - Optional county override for text localization
  # @param {string} [className] - Optional className for the component (also used for styled-components)
*/
const DidYouKnow: React.FC<DidYouKnowProps> = ({
  text,
  headingText,
  icon,
  moreInfoIcon,
  moreInfoText,
  onMoreInfoClick,
  centered,
  expanded,
  expandable,
  compact,
  borderRadius,
  borderColor,
  background = Colors.mint50,
  fullWidth,
  localisationCounty,
  className
}) => {
  const isTextArray = Array.isArray(text);
  const isTextComponent = typeof text !== 'string' && !isTextArray;
  const textArrayLength = isTextArray ? text.length - 1 : 0;
  const [isExpanded, setIsExpanded] = useState(typeof expanded === 'undefined' ? false : expanded);
  const [isRotated, setIsRotated] = useState(!!textArrayLength);
  const [alternatingIndex, setAlternatingIndex] = useState(Math.floor(Math.random() * textArrayLength) + 1);
  const location = useLocation();
  const { country } = useCountry();

  const leftIcon = useMemo(() => renderIcon(icon), [icon]);
  const bottomLeftIcon = useMemo(() => renderIcon(moreInfoIcon), [moreInfoIcon]);

  const rightIcon = useMemo(() => {
    if (isTextArray) {
      return <LazyLoadImage src={REFRESH_ICON} />;
    }
    if (expandable && !isExpanded) {
      return <ChevronDownIcon />;
    }
    if (expandable && isExpanded) {
      return <ChevronUpIcon />;
    }
    return null;
  }, [isTextArray, expandable, isExpanded]);

  const handleHeaderClick = useCallback(() => {
    if (expandable) {
      setIsExpanded((prev) => !prev);
    } else if (textArrayLength && !isTextComponent) {
      setAlternatingIndex((prev) => (prev === text.length - 1 ? 0 : prev + 1));
      setIsRotated((prev) => !prev);
    }
  }, [alternatingIndex]);

  const isLeftIconForBody = useMemo(() => Boolean(leftIcon && text && !headingText && !rightIcon), [leftIcon, text, headingText, rightIcon]);

  const paddingBodyText = useMemo(() => {
    if (!headingText) {
      return '0px';
    }
    if (compact) {
      return '8px';
    }
    return '16px';
  }, [headingText, compact]);

  return (
    <Container
      $fullWidth={fullWidth}
      $borderColor={borderColor}
      $borderRadius={borderRadius}
      $background={background}
      $centered={centered}
      $compact={compact}
      className={className}
    >
      {headingText && (
        <HeadingContainer onClick={handleHeaderClick} $centered={centered} $pointer={!!expandable || !!textArrayLength}>
          {leftIcon ? <LeftIconWrap $centered={centered}>{leftIcon}</LeftIconWrap> : null}
          <HeadingText $mobileVariant={TextVariant.BodyBoldMD} $variant={TextVariant.BodyBoldLG}>
            {headingText}
          </HeadingText>
          {rightIcon ? (
            <RightIconWrap $isRotated={isRotated} $isExpanded={isExpanded} $centered={centered}>
              {rightIcon}
            </RightIconWrap>
          ) : null}
        </HeadingContainer>
      )}
      <ExpandableContent expanded={isExpanded || !expandable} $paddingTop={paddingBodyText}>
        <BodyContainer $compact={compact}>
          <BodyTextContainer>
            {isLeftIconForBody && (
              <LeftIconWrap $centered={centered} $alignSelf={isLeftIconForBody}>
                {leftIcon}
              </LeftIconWrap>
            )}
            <BodyTextDiv $variant={TextVariant.BodyRegularMD}>
              {isTextComponent
                ? text
                : getTranslatedHtml(replaceText(isTextArray ? text[alternatingIndex] : text, localisationCounty || country, location), true)}
            </BodyTextDiv>
          </BodyTextContainer>
          {bottomLeftIcon || moreInfoText ? (
            <MoreInfoContainer $pointer={!!onMoreInfoClick} onClick={() => onMoreInfoClick && onMoreInfoClick()} $centered={centered}>
              <MoreInfoIconWrap>{bottomLeftIcon}</MoreInfoIconWrap>
              <MoreInfoText $variant={TextVariant.BodyBoldSM}>{moreInfoText}</MoreInfoText>
            </MoreInfoContainer>
          ) : null}
        </BodyContainer>
      </ExpandableContent>
    </Container>
  );
};

export default DidYouKnow;

const Container = styled.div<{
  $fullWidth?: boolean;
  $borderColor?: string;
  $borderRadius?: string;
  $background: string;
  $centered?: boolean;
  $compact?: boolean;
}>`
  background: ${(props) => props.$background};
  border-radius: ${(props) => props.$borderRadius || '5px'};
  border: 1px solid ${(props) => props.$borderColor || 'transparent'};
  overflow: hidden;
  font-family: BrownStd;
  color: ${Colors.navyBlue};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  padding: 16px;

  display: flex;
  flex-direction: column;

  ${(props) =>
    props.$centered &&
    css`
      align-items: center;
      text-align: center;

      & > div {
        justify-content: center;
      }
    `};
`;

const HeadingContainer = styled.div<{ $centered?: boolean; $pointer?: boolean }>`
  cursor: ${(props) => (props.$pointer ? 'pointer' : 'initial')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;
const HeadingText = styled(Typography)<{ $centered?: boolean }>`
  flex: ${(props) => (props.$centered ? 'initial' : '1')};

  @media (max-width: ${BREAKPOINT.mobileS}px) {
    font-size: 12px;
  }
`;

const LeftIconWrap = styled.div<{ $centered?: boolean; $alignSelf?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  align-self: ${(props) => (props.$alignSelf ? 'baseline' : 'auto')};

  ${(props) =>
    props.$centered &&
    css`
      justify-content: center;
      align-items: center;
    `};
`;
const RightIconWrap = styled.div<{ $isRotated?: boolean; $isExpanded?: boolean; $centered?: boolean }>`
  cursor: pointer;
  display: flex;

  ${(props) =>
    props.$centered &&
    css`
      justify-content: center;
      align-items: center;
    `};

  img {
    transition: transform 0.2s ease-out;
    transform: ${({ $isRotated }) => `rotate(${$isRotated ? '180' : '0'}deg)`};
  }
  svg {
    height: 16px;
    width: 16px;
  }
`;

const BodyContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? '12px' : '16px')};
`;
const BodyTextContainer = styled.div`
  display: flex;
  gap: 8px;
`;
const BodyTextDiv = styled.div<{ $variant: TextVariant }>`
  ${(props) => TEXT_STYLES[props.$variant]};
  color: ${Colors.navyBlue};

  ul {
    margin-bottom: 0;
    li {
      font-size: 13px;
    }
  }
  a {
    color: ${Colors.lilac};
  }
`;

const MoreInfoContainer = styled.div<{ $pointer: boolean; $centered?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${Colors.lilac};
  cursor: ${(props) => (props.$pointer ? 'pointer' : 'initial')};

  ${(props) =>
    props.$centered &&
    css`
      justify-content: center;
      align-items: center;
    `};
`;
const MoreInfoIconWrap = styled.div`
  height: 16px;
  width: 16px;
  > svg {
    height: 16px;
    width: 16px;
  }
`;
const MoreInfoText = styled(Typography)`
  color: ${Colors.lilac};
`;
